import type { Immutable } from 'immer';
import * as z from 'zod';
import { ZodOverridable } from '../../validation.ts';

export const BoneNameSchema = ZodOverridable(z.string());
export type BoneName = z.infer<typeof BoneNameSchema>;

export type BoneType = 'pose' | 'body';

export const CharacterViewSchema = z.enum(['front', 'back']);
export type CharacterView = z.infer<typeof CharacterViewSchema>;

export const ArmPoseSchema = z.enum(['front_above_hair', 'front', 'back', 'back_below_hair']);
export type ArmPose = z.infer<typeof ArmPoseSchema>;

export const ArmRotationSchema = z.enum(['backward', 'up', 'forward', 'down']);
export type ArmRotation = z.infer<typeof ArmRotationSchema>;

export const ArmFingersSchema = z.enum(['spread', 'fist']);
export type ArmFingers = z.infer<typeof ArmFingersSchema>;

export const ArmSegmentOrderSchema = z.enum(['left', 'right']);
export type ArmSegmentOrder = z.infer<typeof ArmSegmentOrderSchema>;

export const LegSideOrderSchema = z.enum(['left', 'right']);
export type LegSideOrder = z.infer<typeof LegSideOrderSchema>;

export const LegsPoseSchema = z.enum(['standing', 'sitting', 'kneeling']);
export type LegsPose = z.infer<typeof LegsPoseSchema>;

export const CONDITION_EQ_OPERATORS = ['=', '!='] as const;
export const ConditionEqOperatorSchema = z.enum(CONDITION_EQ_OPERATORS);
export type ConditionEqOperator = z.infer<typeof ConditionEqOperatorSchema>;

export const CONDITION_OPERATORS = ['=', '<', '<=', '>', '>=', '!='] as const;
export const ConditionOperatorSchema = z.enum(CONDITION_OPERATORS);
export type ConditionOperator = z.infer<typeof ConditionOperatorSchema>;

export const ModuleNameSchema = ZodOverridable(z.string());
export const AttributeNameSchema = ZodOverridable(z.string());

export const AtomicConditionBoneSchema = z.object({
	bone: BoneNameSchema,
	operator: ConditionOperatorSchema,
	value: z.number(),
});
export type AtomicConditionBone = z.infer<typeof AtomicConditionBoneSchema>;
export const AtomicConditionModuleSchema = z.object({
	module: ModuleNameSchema,
	operator: ConditionEqOperatorSchema,
	value: z.string(),
});
export const AtomicConditionAttributeSchema = z.object({
	/**
	 * Attribute that which required for this condition to be true
	 *  - attribute can be prefixed with `!` to negate the condition
	 */
	attribute: AttributeNameSchema,
});
export const AtomicConditionArmRotationSchema = z.object({
	armType: z.literal('rotation'),
	side: z.enum(['left', 'right']),
	operator: ConditionEqOperatorSchema,
	value: ArmRotationSchema,
});
export const AtomicConditionArmFingersSchema = z.object({
	armType: z.literal('fingers'),
	side: z.enum(['left', 'right']),
	operator: ConditionEqOperatorSchema,
	value: ArmFingersSchema,
});

type Negate<T extends string> = `!${T}`;
type NegateArray<T extends string[]> = T extends [infer F extends string, ...infer R extends string[]] ? [Negate<F>, ...NegateArray<R>] : [];
function Negatable<T extends string[]>(arr: T): [...T, ...NegateArray<T>] {
	return [...arr, ...arr.map((x) => `!${x}`) as NegateArray<T>];
}

export const AtomicConditionLegsSchema = z.object({
	legs: z.enum(Negatable(LegsPoseSchema.options)),
});
export const AtomicConditionViewSchema = z.object({
	view: CharacterViewSchema,
});
export const AtomicConditionBlinkingSchema = z.object({
	blinking: z.boolean(),
});

/** Smallest condition segment that can be used for pose transforms */
export const AtomicPoseConditionSchema = z.union([
	AtomicConditionBoneSchema,
	AtomicConditionArmRotationSchema,
	AtomicConditionArmFingersSchema,
	AtomicConditionLegsSchema,
	AtomicConditionViewSchema,
]);
/** Smallest condition segment that can be used for pose transforms */
export type AtomicPoseCondition = z.infer<typeof AtomicPoseConditionSchema>;

/**
 * A condition usable in posing.
 * Allows only single string of atomic conditions connected by AND operator.
 * If transform needs to be conditioned with OR, it should be duplicated instead.
 */
export const PoseConditionSchema = AtomicPoseConditionSchema.array();
/**
 * A condition usable in posing.
 * Allows only single string of atomic conditions connected by AND operator.
 * If transform needs to be conditioned with OR, it should be duplicated instead.
 */
export type PoseCondition = z.infer<typeof PoseConditionSchema>;

/** Smallest condition segment that can be used graphics decisions */
export const AtomicConditionSchema = z.union([
	AtomicPoseConditionSchema,
	AtomicConditionModuleSchema,
	AtomicConditionAttributeSchema,
	AtomicConditionBlinkingSchema,
]);
/** Smallest condition segment that can be used graphics decisions */
export type AtomicCondition = z.infer<typeof AtomicConditionSchema>;

export const ConditionSchema = z.array(z.array(AtomicConditionSchema));
export type Condition = z.infer<typeof ConditionSchema>;

/**
 * Takes two logic conditions in DNF and produces a new DNF condition that is OR of the two.
 */
export function ConditionsCombineOr(a: Immutable<Condition>, b: Immutable<Condition>): Immutable<Condition> {
	return [...a, ...b];
}
/**
 * Takes two logic conditions in DNF and produces a new DNF condition that is AND of the two.
 */
export function ConditionsCombineAnd(a: Immutable<Condition>, b: Immutable<Condition>): Immutable<Condition> {
	// Result is and between individual clauses, for every OR combination
	// We don't do simplification as usually it is not worth it, as we usually combine with something without OR operators.
	const result: Immutable<AtomicCondition>[][] = [];
	for (const ca of a) {
		for (const cb of b) {
			result.push([...ca, ...cb]);
		}
	}
	return result;
}
